// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../supabase';

// Use your environment-based API URL for all fetches to the Node server
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [role, setRole] = useState('user');
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const initSession = async () => {
      console.log('[DEBUG] localStorage at start =>', JSON.stringify(localStorage));

      // Get the Supabase session
      const { data, error } = await supabase.auth.getSession();
      console.log('[DEBUG] supabase.auth.getSession => data:', data, 'error:', error);
      if (error) {
        console.error('[AuthContext] getSession error:', error.message);
      }

      const currentSession = data?.session || null;
      setSession(currentSession);

      let sessionUser = currentSession?.user || null;
      console.log('[DEBUG] initSession => raw user from session:', sessionUser);

      if (sessionUser) {
        try {
          // fetch the custom user row from the Node backend
          const token = currentSession.access_token;
          const res = await fetch(`${API_BASE}/api/users/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (!res.ok) {
            throw new Error(`GET /api/users/me => ${res.status} ${res.statusText}`);
          }
          const row = await res.json();
          console.log('[DEBUG] /api/users/me =>', row);

          sessionUser = { ...sessionUser, ...row };
          setRole(row.role || 'user');
        } catch (err) {
          console.error('[AuthContext] Error fetching user row:', err.message);
          setRole('user');
        }
      } else {
        console.log('[DEBUG] No user => role=user');
        setRole('user');
      }

      setUser(sessionUser);
      setAuthLoading(false);
    };

    initSession();

    // Listen for sign-in/out changes from Supabase
    const { data: subscription } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log('[DEBUG] onAuthStateChange => event:', event, 'newSession:', newSession);
        setSession(newSession);

        let currentUser = newSession?.user || null;
        if (currentUser) {
          try {
            // fetch the custom user row from the Node backend
            const token = newSession.access_token;
            const res = await fetch(`${API_BASE}/api/users/me`, {
              headers: { Authorization: `Bearer ${token}` },
            });

            if (!res.ok) {
              throw new Error(`GET /api/users/me => ${res.status} ${res.statusText}`);
            }
            const row = await res.json();
            console.log('[DEBUG] (onAuthStateChange) => row:', row);

            currentUser = { ...currentUser, ...row };
            setRole(row.role || 'user');
          } catch (err) {
            console.error('[AuthContext] onAuthStateChange fetch error:', err.message);
            setRole('user');
          }
        } else {
          // Signed out
          console.log('[DEBUG] onAuthStateChange => no user => role=user');
          setRole('user');
        }

        setUser(currentUser);
        setAuthLoading(false);
      }
    );

    return () => {
      subscription?.subscription.unsubscribe();
    };
  }, []);

  const getToken = () => session?.access_token || null;

  /**
   * signUp:
   * 1) Create Supabase Auth user
   * 2) If we have a session, call /api/users/create-row to create the user row
   *    pass along firstName + wantsUpdates
   */
  const signUp = async (email, password, firstName, wantsUpdates = false) => {
    if (!firstName || !firstName.trim()) {
      throw new Error('First name is required.');
    }

    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) {
      console.error('[AuthContext] signUp error:', error.message);
      throw new Error(error.message);
    }
    console.log('[DEBUG] signUp => new userId:', data.user?.id);

    // If signUp returned a session, create the user row
    if (data.session) {
      try {
        const token = data.session.access_token;
        const res = await fetch(`${API_BASE}/api/users/create-row`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            firstName,
            wantsUpdates
          }),
        });

        if (!res.ok) {
          throw new Error(`POST /api/users/create-row => ${res.status} ${res.statusText}`);
        }
        console.log('[DEBUG] user row creation success');
      } catch (err2) {
        console.error('[AuthContext] user row creation error:', err2.message);
      }
    }
  };

  /**
   * signIn:
   */
  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      console.error('[AuthContext] signIn error:', error.message);
      throw new Error(error.message);
    }
    console.log('[DEBUG] signIn => data:', data);
  };

  /**
   * logout:
   */
  const logout = async () => {
    console.log('[DEBUG] logout invoked');
    console.log('[DEBUG] localStorage before signOut =>', JSON.stringify(localStorage));

    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('[AuthContext] signOut error:', error.message);
      } else {
        console.log('[DEBUG] supabase.auth.signOut => success');
      }
    } catch (err) {
      console.error('[AuthContext] signOut catch error:', err.message);
    }

    console.log('[DEBUG] localStorage after signOut =>', JSON.stringify(localStorage));
    setUser(null);
    setSession(null);
    setRole('user');
    console.log('[DEBUG] Post-signOut => user=null, role=user');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        role,
        authLoading,
        signUp,
        signIn,
        logout,
        getToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
